import React, { useState, useEffect } from 'react';
import { apiFetch, useAuth, useToast } from '../../App';
import { Gift, Copy, Check, Users, TrendingUp, Share2, ArrowRight } from 'lucide-react';

export default function PawsReferral() {
  const { user, refreshUser } = useAuth();
  const addToast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [referralInput, setReferralInput] = useState('');
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    apiFetch('/api/referral').then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const copyCode = () => {
    if (data?.referralCode) {
      navigator.clipboard.writeText(data.referralCode).catch(() => {});
      setCopied(true);
      addToast('Referral code copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareLink = () => {
    const msg = `Join Petfolk for the best pet grooming! Use my referral code ${data?.referralCode} and get ₹100 credits. Book now at petfolk.in`;
    if (navigator.share) {
      navigator.share({ title: 'Petfolk Referral', text: msg }).catch(() => {});
    } else {
      navigator.clipboard.writeText(msg).catch(() => {});
      addToast('Share link copied!');
    }
  };

  const handleApplyReferral = async () => {
    if (!referralInput.trim()) return;
    setApplying(true);
    try {
      const res = await apiFetch('/api/referral/apply', {
        method: 'POST',
        body: JSON.stringify({ referralCode: referralInput.trim().toUpperCase() }),
      });
      addToast(res.message || `You earned ₹${res.bonusEarned} credits!`);
      setReferralInput('');
      refreshUser();
      // Reload referral data
      const d = await apiFetch('/api/referral');
      setData(d);
    } catch (e) {
      addToast(e.message, 'error');
    }
    setApplying(false);
  };

  if (loading) return (
    <div className="space-y-4 max-w-4xl">
      <div className="skeleton h-10 w-48 rounded-xl" />
      <div className="skeleton h-48 rounded-2xl" />
      <div className="skeleton h-32 rounded-2xl" />
    </div>
  );

  return (
    <div data-testid="paws-referral" className="max-w-4xl">
      <h1 className="font-outfit text-2xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>Refer & Earn</h1>

      {/* Hero Card */}
      <div className="rounded-3xl p-8 mb-8 text-white relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #FF8C42 0%, #F57C00 50%, #8B5CF6 100%)' }} data-testid="referral-hero">
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <Gift size={28} />
            <h2 className="font-outfit text-2xl font-bold">Share the Love</h2>
          </div>
          <p className="text-white/90 mb-6 max-w-lg">
            Invite friends to Petfolk and earn <span className="font-bold">₹{data?.bonusPerReferral || 150} credits</span> for each friend who signs up. 
            They get <span className="font-bold">₹{data?.refereeBonus || 100} credits</span> too!
          </p>

          {/* Referral Code */}
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-white/20 rounded-2xl px-6 py-3 flex items-center gap-3">
              <span className="font-mono text-xl font-bold tracking-[0.2em]">{data?.referralCode}</span>
              <button onClick={copyCode} className="p-2 rounded-xl bg-white/20 hover:bg-white/30 transition-colors" data-testid="copy-referral-code">
                {copied ? <Check size={18} /> : <Copy size={18} />}
              </button>
            </div>
            <button onClick={shareLink} className="px-5 py-3 rounded-2xl bg-white text-petfolk-orange font-semibold flex items-center gap-2 hover:scale-105 transition-transform" data-testid="share-referral">
              <Share2 size={16} /> Share
            </button>
          </div>
        </div>
        <div className="absolute top-4 right-4 w-32 h-32 rounded-full bg-white/10" />
        <div className="absolute -bottom-6 -right-6 w-40 h-40 rounded-full bg-white/5" />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="petfolk-card p-5 text-center" data-testid="referral-count">
          <Users size={22} className="mx-auto mb-2 text-petfolk-purple" />
          <p className="font-outfit text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{data?.referralCount || 0}</p>
          <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Friends Referred</p>
        </div>
        <div className="petfolk-card p-5 text-center" data-testid="total-earned">
          <TrendingUp size={22} className="mx-auto mb-2 text-green-500" />
          <p className="font-outfit text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>₹{data?.totalEarned || 0}</p>
          <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Total Earned</p>
        </div>
        <div className="petfolk-card p-5 text-center">
          <Gift size={22} className="mx-auto mb-2 text-petfolk-orange" />
          <p className="font-outfit text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>₹{data?.bonusPerReferral || 150}</p>
          <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Per Referral</p>
        </div>
      </div>

      {/* Apply Referral Code */}
      {!user?.referredBy && (
        <div className="petfolk-card p-6 mb-8" data-testid="apply-referral-section">
          <h3 className="font-outfit font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Have a referral code?</h3>
          <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>Enter a friend's referral code to get ₹{data?.refereeBonus || 100} credits</p>
          <div className="flex gap-2">
            <input
              className="petfolk-input flex-1 font-mono uppercase"
              placeholder="Enter code"
              value={referralInput}
              onChange={e => setReferralInput(e.target.value.toUpperCase())}
              data-testid="referral-code-input"
            />
            <button onClick={handleApplyReferral} disabled={applying} className="btn-primary px-6" data-testid="apply-referral-btn">
              Apply
            </button>
          </div>
        </div>
      )}

      {/* How it Works */}
      <div className="petfolk-card p-6 mb-8">
        <h3 className="font-outfit font-bold mb-4" style={{ color: 'var(--text-primary)' }}>How it Works</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { step: '1', title: 'Share Your Code', desc: 'Copy your unique referral code and share with friends' },
            { step: '2', title: 'Friend Signs Up', desc: 'Your friend creates a Petfolk account using your code' },
            { step: '3', title: 'Both Earn Credits', desc: `You get ₹${data?.bonusPerReferral || 150}, they get ₹${data?.refereeBonus || 100}` },
          ].map(s => (
            <div key={s.step} className="text-center">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-petfolk-orange to-petfolk-purple flex items-center justify-center mx-auto mb-3 text-white font-bold">
                {s.step}
              </div>
              <h4 className="font-outfit font-semibold text-sm mb-1" style={{ color: 'var(--text-primary)' }}>{s.title}</h4>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{s.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Referral History */}
      {data?.referrals?.length > 0 && (
        <div>
          <h3 className="font-outfit font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Referral History</h3>
          <div className="space-y-2">
            {data.referrals.map((r, i) => (
              <div key={i} className="petfolk-card p-4 flex items-center gap-4" data-testid={`referral-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Check size={18} className="text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{r.refereeName || 'Friend'} joined</p>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{new Date(r.createdAt).toLocaleDateString('en-IN')}</p>
                </div>
                <span className="font-outfit font-bold text-green-600">+₹{r.bonusAmount}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
